import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { CanvasMark } from '../plane/CanvasMark';
import { useVault } from '../session/useVault';
import { pendingText } from '../vault/writeQueue';
import { itemFor } from '../thoughts/corpus';
import { NookItemCard } from './NookItemCard';
import { flat } from '../entity/entity';
import type { Arc } from '../plan/arc';
import { statusVar } from '../plan/arc';
import { StatusGlyph } from '../board/StatusGlyph';
import { arcHref } from '../routes/ArcRoute';
import {
  capturedLabel,
  recordingWords,
} from './nook';
import { ArcLine, CloseGlyph, NoteGlyph } from './parts';
import { Player, RecordingDoc } from '../doc/RecordingDoc';
import { VaultAudio } from '../doc/VaultAudio';
import { Face } from '../doc/Face';
import { DetailsHead, NookDetails, WideColumns } from './HangShells';
import type { FullScreen } from './HangShells';
import { hrefFor } from '../routes/EntityPage';
import { heldArcsOf, waitingOnYou } from '../workspaces/workspace';
import type { Workspace } from '../workspaces/workspace';

/** An arc a recording is in: a chip in the arc's own status, opened in place. */
function InChip({ arc, onOpen }: { arc: Arc; onOpen: () => void }) {
  return (
    <button
      type="button"
      className="nook-chip"
      style={{ background: `rgb(from var(${statusVar(arc.status)}) r g b / 0.13)` }}
      onClick={onOpen}
    >
      <StatusGlyph status={arc.status} size={9} />
      {arc.title}
    </button>
  );
}

export function RecordingWindow({
  path,
  raw,
  anchor,
  inArcs,
  onOpenArc,
  onExpand,
  fullScreen,
  onRemove,
  onClose,
}: {
  path: string;
  raw: string;
  anchor: string | undefined;
  inArcs: Arc[];
  onOpenArc: (arc: Arc) => void;
  /** Opens the recording as a document over the page; Full screen unless [fullScreen] says. */
  onExpand?: () => void;
  fullScreen?: FullScreen;
  onRemove?: () => void;
  onClose: () => void;
}) {
  const date = capturedLabel(path, raw);
  return (
    <>
      <DetailsHead lead={<span className="nook-date">{date}</span>} fullScreen={fullScreen ?? (onExpand ? { onPress: onExpand } : undefined)} onClose={onClose} />
      <div className="nook-window-body">
        <WideColumns
          about={
            <>
              <VaultAudio notePath={path} size={44} fallback={<Player size={44} />} />
              {inArcs.length > 0 && (
                <div className="nook-chips">
                  <span className="nook-chips-label">In</span>
                  {inArcs.map((arc) => (
                    <InChip key={arc.path} arc={arc} onOpen={() => onOpenArc(arc)} />
                  ))}
                </div>
              )}
              {onRemove && (
                <button type="button" className="nook-window-link nook-window-remove" onClick={onRemove}>
                  Remove from here
                </button>
              )}
            </>
          }
          holds={(() => {
            const opening = recordingWords(path, raw, anchor);
            return <Face lines={opening.lines} picked={opening.picked} className="nook-window-words" />;
          })()}
        />
      </div>
    </>
  );
}

/** A note, from the desk: what it says, and its page as Full screen. */
export function NoteWindow({
  title,
  text,
  fullScreen,
  onClose,
}: {
  title: string | null;
  text: string;
  fullScreen?: FullScreen;
  onClose: () => void;
}) {
  return (
    <>
      <DetailsHead lead={<NoteGlyph />} fullScreen={fullScreen} onClose={onClose} />
      <div className="nook-window-body">
        {title && <h2 className="hang-name">{title}</h2>}
        {text && <p className="hang-prose">{text}</p>}
      </div>
    </>
  );
}

/**
 * An arc opened in place: what it is, what finishing looks like, and where to
 * start. Its own page is a link inside the window, not the click that opened
 * it.
 */
export function ArcWindow({
  arc,
  onRemove,
  onClose,
}: {
  arc: Arc;
  onRemove?: () => void;
  onClose: () => void;
}) {
  const doneWhen = flat(arc.frontMatter['done-when']);
  const startWith = flat(arc.frontMatter['start-with']);
  const needs = flat(arc.frontMatter['needs']);
  return (
    <>
      <DetailsHead lead={<StatusGlyph status={arc.status} size={18} />} fullScreen={{ to: arcHref(arc.name) }} onClose={onClose} />
      <div className="nook-window-body">
        <WideColumns
          about={
            <>
              <h2 className="nook-window-arc">
                <ArcLine title={arc.title} status={arc.status} />
              </h2>
              {arc.status === 'needs-input' && needs && <p className="nook-window-needs">{needs}</p>}
              {onRemove && (
                <button type="button" className="nook-window-link nook-window-remove" onClick={onRemove}>
                  Remove from here
                </button>
              )}
            </>
          }
          holds={
            doneWhen || startWith ? (
              <>
                {doneWhen && (
                  <div className="nook-window-field">
                    <span className="nook-window-label">Done when</span>
                    <p>{doneWhen}</p>
                  </div>
                )}
                {startWith && (
                  <div className="nook-window-field">
                    <span className="nook-window-label">First step</span>
                    <p>{startWith}</p>
                  </div>
                )}
              </>
            ) : undefined
          }
        />
      </div>
    </>
  );
}

const HELD_RECORDINGS = 4;
const HELD_NOTES = 3;

/** The recordings and notes a nook holds, as the cards drawn everywhere else: the newest few of
 *  each, and how many more there are. Nothing held leaves the column out. */
function NookHolds({ nook, own, onOpen }: { nook: Workspace; own: boolean; onOpen?: (ref: { ref: string; anchors: string[] }, kind: 'recording' | 'note') => void }) {
  const { state } = useVault();
  const held = useMemo(() => {
    const seen = new Set<string>();
    const out: { ref: string; anchors: string[]; kind: 'recording' | 'note'; at: number }[] = [];
    for (const m of nook.sources) {
      const item = itemFor(m.path);
      const key = m.anchor ? `${m.path}#${m.anchor}` : m.path;
      if ((item.kind !== 'recording' && item.kind !== 'note') || seen.has(key)) continue;
      seen.add(key);
      out.push({ ref: m.path, anchors: m.anchor ? [m.anchor] : [], kind: item.kind, at: item.at?.getTime() ?? 0 });
    }
    return out.sort((a, b) => b.at - a.at);
  }, [nook]);
  const recordings = held.filter((h) => h.kind === 'recording');
  const notes = held.filter((h) => h.kind === 'note');
  const shown = [...recordings.slice(0, HELD_RECORDINGS), ...notes.slice(0, HELD_NOTES)];
  const more = recordings.length - Math.min(recordings.length, HELD_RECORDINGS);
  const moreNotes = notes.length - Math.min(notes.length, HELD_NOTES);
  const [files, setFiles] = useState<ReadonlyMap<string, string | null>>(new Map());
  const wanted = shown.map((h) => h.ref).join('\n');
  useEffect(() => {
    if (state.status !== 'ready' || !wanted) return;
    let cancelled = false;
    const paths = wanted.split('\n');
    void state.vault.readFiles(paths).then((read) => {
      if (!cancelled) setFiles(new Map(read.map((r) => [r.path, pendingText(r.path) ?? r.text ?? null])));
    });
    return () => {
      cancelled = true;
    };
  }, [state, wanted]);
  if (shown.length === 0) return null;
  const rest = [more > 0 ? `${more} more recording${more === 1 ? '' : 's'}` : '', moreNotes > 0 ? `${moreNotes} more note${moreNotes === 1 ? '' : 's'}` : ''].filter(Boolean).join(' · ');
  return (
    <>
      <div className="hang-held">
        {shown.map((h) => (
          <NookItemCard
            key={`${h.ref}#${h.anchors[0] ?? ''}`}
            path={h.ref}
            anchor={h.anchors[0]}
            kind={h.kind}
            text={files.get(h.ref) ?? null}
            nookTags={nook.tags}
            onOpen={() => onOpen?.({ ref: h.ref, anchors: h.anchors }, h.kind)}
          />
        ))}
      </div>
      {rest && (own ? <span className="hang-more">{rest}</span> : <Link className="hang-more" to={hrefFor(nook.path)}>{rest}</Link>)}
    </>
  );
}

/**
 * A nook opened in place: what its card says — where it stands, its first
 * step, what waits on you — and the arcs it is working through. Another
 * nook's window is the way into it; the nook being sat in opens its own.
 */
export function NookWindow({
  nook,
  name,
  arcsByName,
  ink,
  own,
  startWith = nook.startWith,
  onOpenArc,
  onOpenHeld,
  onClose,
}: {
  nook: Workspace;
  /** Empty for a nook nobody named. */
  name: string;
  arcsByName: ReadonlyMap<string, Arc> | null;
  ink?: string;
  /** The nook being sat in: no way into where you already are. */
  own: boolean;
  /** Its first step, where it borrows one from its first open arc. */
  startWith?: string | null;
  onOpenArc: (arc: Arc) => void;
  /** Opens a recording or note it holds, from its card. */
  onOpenHeld?: (ref: { ref: string; anchors: string[] }, kind: 'recording' | 'note') => void;
  onClose: () => void;
}) {
  const waiting = waitingOnYou(nook, arcsByName);
  const live = arcsByName ? heldArcsOf(nook, arcsByName).filter((arc) => arc.status !== 'done') : [];
  return (
    <NookDetails
      name={name}
      ink={ink}
      waiting={waiting}
      glance={nook.glance}
      next={startWith}
      holds={<NookHolds nook={nook} own={own} onOpen={onOpenHeld} />}
      fullScreen={own ? undefined : { to: hrefFor(nook.path) }}
      onClose={onClose}
      lines={live.map((arc) => (
        <button key={arc.path} type="button" className="nook-row" onClick={() => onOpenArc(arc)}>
          <ArcLine title={arc.title} status={arc.status} />
        </button>
      ))}
    />
  );
}

/**
 * A recording read in full, over the plane: its words, then the transcript,
 * the highlights and the arcs it is in behind quiet switches.
 */
export function DocumentSheet({
  path,
  raw,
  anchor,
  inArcs,
  placed,
  onPlace,
  onOpenArc,
  onClose,
}: {
  path: string;
  raw: string;
  anchor: string | undefined;
  inArcs: Arc[];
  placed: boolean;
  onPlace: () => void;
  onOpenArc: (arc: Arc) => void;
  onClose: () => void;
}) {
  return (
    <>
      <div className="nook-scrim" aria-hidden="true" onClick={onClose} />
      <article className="nook-sheet" aria-label="Recording">
        <div className="nook-sheet-head">
          {placed ? (
            <span className="nook-placed">
              <CanvasMark />
            </span>
          ) : (
            <button type="button" className="nook-btn" onClick={onPlace}>
              Keep on the canvas
            </button>
          )}
          <button type="button" className="nook-btn nook-icon" aria-label="Close" onClick={onClose}>
            <CloseGlyph />
          </button>
        </div>
        <div className="nook-sheet-column">
          <RecordingDoc
            path={path}
            raw={raw}
            anchor={anchor}
            extra={
              inArcs.length > 0
                ? {
                    label: `In ${inArcs.length} ${inArcs.length === 1 ? 'arc' : 'arcs'}`,
                    render: () => (
                      <div className="nook-sheet-arcs">
                        {inArcs.map((arc) => (
                          <button key={arc.path} type="button" className="nook-row" onClick={() => onOpenArc(arc)}>
                            <ArcLine title={arc.title} status={arc.status} />
                          </button>
                        ))}
                      </div>
                    ),
                  }
                : undefined
            }
          />
        </div>
      </article>
    </>
  );
}
